import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { INHALT_GEFAHREN, type GefahrRowState, type GefahrenMatrixState } from "./gefahrenMatrixData";

const useStyles = makeStyles({
  wrap: {
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  helper: { fontFamily: BRAND.fontPrimary, fontSize: "0.85rem", color: BRAND.muted },
  tableWrap: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.ink,
    minWidth: "920px",
  },
  th: {
    textAlign: "left",
    fontWeight: 700,
    fontSize: "0.78rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: BRAND.muted,
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
    backgroundColor: BRAND.bg,
    whiteSpace: "nowrap",
  },
  thCenter: { textAlign: "center" },
  thNum: { textAlign: "right" },
  td: {
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderBottom("1px", "dashed", BRAND.borderDashed),
    verticalAlign: "middle",
  },
  tdCenter: { textAlign: "center" },
  tdMuted: { color: BRAND.muted, fontSize: "0.82rem" },
  rowInactive: { backgroundColor: BRAND.bg, opacity: 0.55 },
  rowActive: { backgroundColor: "rgba(46,125,79,0.04)" },
  ziffer: {
    fontWeight: 700,
    color: BRAND.purple,
    fontFamily: BRAND.fontAccent,
  },
  label: {
    fontWeight: 600,
    color: BRAND.ink,
    whiteSpace: "normal",
  },
  paragraph: {
    fontSize: "0.76rem",
    color: BRAND.muted,
    fontStyle: "italic",
    marginTop: "2px",
  },
  checkbox: {
    width: "18px",
    height: "18px",
    cursor: "pointer",
    accentColor: BRAND.pillarGreen,
  },
  numInput: {
    boxSizing: "border-box",
    width: "100%",
    minWidth: "110px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    ...shorthands.borderRadius("6px"),
    outlineWidth: 0,
    textAlign: "right",
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
    ":disabled": {
      backgroundColor: BRAND.bg,
      color: BRAND.muted,
      cursor: "not-allowed",
    },
  },
  toggleCell: { textAlign: "center" },
  counter: {
    fontSize: "0.82rem",
    color: BRAND.muted,
    paddingTop: "4px",
  },
});

type Props = {
  value: GefahrenMatrixState;
  onChange: (next: GefahrenMatrixState) => void;
  /** Wenn true (Inhaltsversicherung): zeige die zusätzliche Ertragsausfall-Spalte. */
  showErtragsausfall: boolean;
};

const GefahrenMatrix = ({ value, onChange, showErtragsausfall }: Props) => {
  const styles = useStyles();
  const update = (key: string, patch: Partial<GefahrRowState>) => {
    const current = value[key] ?? {
      versichert: false,
      hoechstentschaedigung: "",
      jahreshoechstentschaedigung: "",
      selbstbeteiligung: "",
      ertragsausfall: false,
    };
    onChange({ ...value, [key]: { ...current, ...patch } });
  };

  const totalActive = INHALT_GEFAHREN.filter((g) => value[g.key]?.versichert).length;

  return (
    <div className={styles.wrap}>
      <p className={styles.helper}>
        Wählen Sie die zu versichernden Gefahren und tragen Sie pro Gefahr Höchstentschädigung,
        Jahreshöchstentschädigung und Selbstbeteiligung ein.
        {showErtragsausfall &&
          " Bei der Inhaltsversicherung können Sie zusätzlich pro Gefahr den Ertragsausfall mitversichern."}
      </p>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={mergeClasses(styles.th, styles.thCenter)}>Versichern</th>
              <th className={mergeClasses(styles.th, styles.thNum)}>Ziffer</th>
              <th className={styles.th}>Versicherte Gefahr</th>
              <th className={mergeClasses(styles.th, styles.thNum)}>Höchstentschädigung</th>
              <th className={mergeClasses(styles.th, styles.thNum)}>Jahreshöchstentschädigung</th>
              <th className={mergeClasses(styles.th, styles.thNum)}>Selbstbeteiligung</th>
              {showErtragsausfall && (
                <th className={mergeClasses(styles.th, styles.thCenter)}>Ertragsausfall</th>
              )}
            </tr>
          </thead>
          <tbody>
            {INHALT_GEFAHREN.map((g) => {
              const row = value[g.key] ?? {
                versichert: false,
                hoechstentschaedigung: "",
                jahreshoechstentschaedigung: "",
                selbstbeteiligung: "",
                ertragsausfall: false,
              };
              const disabled = !row.versichert;
              return (
                <tr
                  key={g.key}
                  className={row.versichert ? styles.rowActive : styles.rowInactive}
                >
                  <td className={mergeClasses(styles.td, styles.tdCenter)}>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={row.versichert}
                      onChange={(e) => update(g.key, { versichert: e.target.checked })}
                      aria-label={`${g.label} versichern`}
                    />
                  </td>
                  <td className={mergeClasses(styles.td, styles.thNum)}>
                    <span className={styles.ziffer}>{g.ziffer}</span>
                  </td>
                  <td className={styles.td}>
                    <div className={styles.label}>{g.label}</div>
                    {g.paragraph && <div className={styles.paragraph}>{g.paragraph}</div>}
                  </td>
                  <td className={styles.td}>
                    <input
                      className={styles.numInput}
                      type="text"
                      inputMode="decimal"
                      disabled={disabled}
                      placeholder="€"
                      value={row.hoechstentschaedigung}
                      onChange={(e) =>
                        update(g.key, { hoechstentschaedigung: e.target.value })
                      }
                    />
                  </td>
                  <td className={styles.td}>
                    <input
                      className={styles.numInput}
                      type="text"
                      inputMode="decimal"
                      disabled={disabled}
                      placeholder="€"
                      value={row.jahreshoechstentschaedigung}
                      onChange={(e) =>
                        update(g.key, { jahreshoechstentschaedigung: e.target.value })
                      }
                    />
                  </td>
                  <td className={styles.td}>
                    <input
                      className={styles.numInput}
                      type="text"
                      inputMode="decimal"
                      disabled={disabled}
                      placeholder="€"
                      value={row.selbstbeteiligung}
                      onChange={(e) => update(g.key, { selbstbeteiligung: e.target.value })}
                    />
                  </td>
                  {showErtragsausfall && (
                    <td className={mergeClasses(styles.td, styles.toggleCell)}>
                      {g.mitErtragsausfall ? (
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={row.ertragsausfall}
                          disabled={disabled}
                          onChange={(e) =>
                            update(g.key, { ertragsausfall: e.target.checked })
                          }
                          aria-label={`${g.label} mit Ertragsausfall`}
                        />
                      ) : (
                        <span className={styles.tdMuted}>—</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className={styles.counter}>
        {totalActive} von {INHALT_GEFAHREN.length} Gefahren ausgewählt
      </div>
    </div>
  );
};

export default GefahrenMatrix;
